/**
 * Property Context
 *
 * Manages current property and data source selection
 * Syncs data source selection with URL state
 */

import {
  createContext,
  useContext,
  useMemo,
  useCallback,
} from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";
import { useURLState } from "@/hooks/useURLState";
import { getProperty } from "@/lib/api/properties";
import { parseSlugId } from "@/lib/routing/slug-id";
import type { Property, DataSource } from "@/lib/types/property";

interface PropertyContextType {
  property: Property | null;
  isLoading: boolean;
  error: Error | null;
  dataSources: DataSource[];
  activeDataSources: DataSource[];
  selectedSourceIds: string[];
  toggleSource: (sourceId: string) => void;
  selectAllSources: () => void;
  clearSources: () => void;
  refetch: () => void;
}

const PropertyContext = createContext<PropertyContextType | undefined>(
  undefined
);

export function PropertyProvider({ children }: { children: ReactNode }) {
  const { propertySlugId } = useParams<{ propertySlugId: string }>();
  const { token } = useAuth();
  const { state, updateState } = useURLState();

  // Extract property ID from slug
  const propertyId = useMemo(() => {
    if (!propertySlugId) return null;
    return parseSlugId(propertySlugId).id;
  }, [propertySlugId]);

  // Fetch property data
  const {
    data: property,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["property", propertyId],
    queryFn: () => getProperty(propertyId!, token!),
    enabled: !!propertyId && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const dataSources = property?.dataSources || [];

  // Get selected sources from URL, default to all active if none selected
  const selectedSourceIds = useMemo(() => {
    if (state.sources.length > 0) {
      return state.sources;
    }
    // Default to all active sources
    return dataSources.filter((ds) => ds.isActive).map((ds) => ds.dataSourceId);
  }, [state.sources, dataSources]);

  const activeDataSources = dataSources.filter((ds) =>
    selectedSourceIds.includes(ds.dataSourceId)
  );

  const toggleSource = useCallback(
    (sourceId: string) => {
      const newSources = selectedSourceIds.includes(sourceId)
        ? selectedSourceIds.filter((id) => id !== sourceId)
        : [...selectedSourceIds, sourceId];

      updateState({ sources: newSources });
    },
    [selectedSourceIds, updateState]
  );

  const selectAllSources = useCallback(() => {
    const allIds = dataSources
      .filter((ds) => ds.isActive)
      .map((ds) => ds.dataSourceId);
    updateState({ sources: allIds });
  }, [dataSources, updateState]);

  const clearSources = useCallback(() => {
    updateState({ sources: [] });
  }, [updateState]);

  return (
    <PropertyContext.Provider
      value={{
        property: property || null,
        isLoading,
        error: error as Error | null,
        dataSources,
        activeDataSources,
        selectedSourceIds,
        toggleSource,
        selectAllSources,
        clearSources,
        refetch,
      }}
    >
      {children}
    </PropertyContext.Provider>
  );
}

function useProperty() {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error("useProperty must be used within PropertyProvider");
  }
  return context;
}

// Safe hook that returns undefined if provider is not available
export function usePropertySafe() {
  return useContext(PropertyContext);
}
